/**
 * The icon set.
 *
 * Drawn here rather than fetched, for the same reasons `Die` is: the glyphs these replaced
 * rendered differently on every device, because `★`, `✎` and `🗑` are whatever the installed
 * emoji font decides they are — text sitting in a button, sized by a font metric nobody
 * chose, which is why they never filled their buttons.
 *
 * ## House style
 *
 * Bold, round, and filling the box. Every icon works to these rules, because a set that
 * varies in weight or in how much of its viewBox it uses reads as a set of accidents:
 *
 * - **Fill the 24-unit box.** Shapes run to roughly 2–22 on both axes. An icon using only
 *   the middle half looks shrunken beside its neighbours.
 * - **Stroke 2.2** for outlines, 2.4 where the shape is one gesture (the Z in snooze).
 * - **Round caps and joins, always.** No sharp angles anywhere in the set.
 * - **`currentColor` throughout**, so a button's hover and active states carry.
 * - **`size` in nominal pixels, emitted as rem**, so an icon keeps its proportion to the
 *   text beside it when the reader scales up. CSS may override it — `.badge > .icon` sizes
 *   in `em` so a badge's icon tracks the badge's own font size.
 */

/** The root font size the nominal `size` values were chosen against. */
const BASE_FONT_PX = 16;

type IconProps = {
  size?: number;
  title?: string;
  className?: string;
};

/** Shared across the set: the box, the sizing, and how it is announced. */
function Svg({
  size = 16,
  boxHeight = 24,
  title,
  className,
  children,
}: IconProps & {
  children: React.ReactNode;
  /**
   * The viewBox's height in SVG units, 24 (square, matching every other icon) unless a
   * shape's proportions genuinely don't fit a square — the open padlock's shackle, swung
   * clear instead of latched, is taller than it is wide the way the object itself would be.
   * `size` still governs *width*; height follows this ratio, so the icon grows taller
   * without also growing wider than its neighbours.
   */
  boxHeight?: number;
}) {
  return (
    <svg
      width={`${size / BASE_FONT_PX}rem`}
      height={`${(size * (boxHeight / 24)) / BASE_FONT_PX}rem`}
      viewBox={`0 0 24 ${boxHeight}`}
      // Decorative by default: every one of these sits in a button or beside a label that
      // already names it, and a second name only makes a screen reader say it twice.
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
      className={className ? `icon ${className}` : "icon"}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

/** The rounded-stroke defaults every outline icon in the set shares. */
const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/**
 * A five-point star, filled or hollow.
 *
 * Outer radius 10, inner 4.1, round joins — which is what stops the tips looking like
 * needles at badge size. Hollow is the same path stroked, so the two states are the same
 * shape and the button does not jump as it toggles.
 */
const STAR =
  "M12 2 L14.41 8.68 L21.51 8.91 L15.90 13.27 L17.88 20.09 " +
  "L12 16.10 L6.12 20.09 L8.10 13.27 L2.49 8.91 L9.59 8.68 Z";

export function Star({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <Svg {...props} className={filled ? "icon-star filled" : "icon-star"}>
      <path
        d={STAR}
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth={filled ? 1.8 : 2.2}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/**
 * A pencil on the diagonal, nib to the bottom left.
 *
 * Outline rather than solid: at this weight a filled pencil is a black lozenge, and the two
 * cross-lines — the nib and the band below the eraser — are what make it read as a pencil
 * rather than a stick. Runs corner to corner, so it fills the box the way the star does.
 */
export function Pencil(props: IconProps) {
  return (
    <Svg {...props} className="icon-pencil">
      <g {...STROKE}>
        <path d="M17.3 2.4 L21.6 6.7 L8.6 19.7 L2.6 21.4 L4.3 15.4 Z" />
        <path d="M4.3 15.4 L8.6 19.7" />
        <path d="M14.2 5.5 L18.5 9.8" />
      </g>
    </Svg>
  );
}

/** A lidded bin: straight sides, two ribs, filling the box. */
export function Trash(props: IconProps) {
  return (
    <Svg {...props} className="icon-trash">
      <g {...STROKE}>
        {/* Handle, lid, then a body with barely any taper. */}
        <path d="M9 5.4 V3.9 a1.4 1.4 0 0 1 1.4 -1.4 h3.2 a1.4 1.4 0 0 1 1.4 1.4 V5.4" />
        <path d="M2.6 5.6 H21.4" />
        <path d="M5.4 5.6 V19.4 a2.1 2.1 0 0 0 2.1 2.1 h9 a2.1 2.1 0 0 0 2.1 -2.1 V5.6" />
        <path d="M10 10 V17.4 M14 10 V17.4" />
      </g>
    </Svg>
  );
}

/**
 * Copy — two overlapping rounded squares, the back one hollow, the front one filled. The
 * universal clipboard glyph, drawn instead of reused from a font for the same reason every
 * icon in this set is (see this file's own docstring). Corner to corner like `Pencil` and
 * `Star`, not shrunk into the middle of the box.
 */
export function Copy(props: IconProps) {
  return (
    <Svg {...props} className="icon-copy">
      <rect x="2" y="2" width="15" height="15" rx="3" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <rect x="7" y="7" width="15" height="15" rx="3" fill="currentColor" />
    </Svg>
  );
}

/**
 * A bold exclamation — the "important" flag.
 *
 * Wide and round at the top, tapering to a near-point above the dot, so it still reads as
 * emphatic at 13px where the text `!` was a hairline. Solid rather than stroked: this one
 * is a mark, not an object.
 */
export function Important(props: IconProps) {
  return (
    <Svg {...props} className="icon-important">
      <path
        d="M8.6 5.1 a3.4 3.4 0 0 1 6.8 0 L13.3 15.1 a1.35 1.35 0 0 1 -2.6 0 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="19.7" r="2.7" fill="currentColor" />
    </Svg>
  );
}

/** A tick, for a completed win. */
export function Check(props: IconProps) {
  return (
    <Svg {...props} className="icon-check">
      <path d="M3.4 12.6 L9.3 18.6 L20.6 5.4" {...STROKE} strokeWidth="2.8" />
    </Svg>
  );
}

/**
 * A clock — a due date.
 *
 * Hands at ten-past-ten, the arrangement that reads as "clock" fastest and stays legible
 * when the whole thing is 13px wide in a badge.
 */
export function Clock(props: IconProps) {
  return (
    <Svg {...props} className="icon-clock">
      <g {...STROKE}>
        <circle cx="12" cy="12" r="9.4" />
        <path d="M12 6.2 V12 L16.4 14.6" />
      </g>
    </Svg>
  );
}

/**
 * A stopwatch — an estimate.
 *
 * Case, crown and side button from one reference, the dial from the other. The dial is a
 * three-quarter pie rather than a hand: it borrows the pomodoro reading — a portion of time,
 * consumed — and, unlike a hand, a filled wedge survives being shrunk because it is an area
 * rather than a line. The missing quarter is the top left: a mirror about the dial's
 * *vertical* axis, so x reflects about 12 and the sweep flag inverts.
 */
export function Stopwatch(props: IconProps) {
  return (
    <Svg {...props} className="icon-stopwatch">
      <g {...STROKE}>
        <path d="M9.2 2.2 H14.8" />
        <path d="M12 2.2 V5.2" />
        <circle cx="12" cy="13.6" r="8.2" />
        <path d="M19 7.2 L20.9 5.3" />
      </g>
      <path d="M6.6 13.6 A5.4 5.4 0 1 0 12 8.2 L12 13.6 Z" fill="currentColor" />
    </Svg>
  );
}

/**
 * Two circular arrows — a repeating task.
 *
 * Two segments rather than one loop: the pair is what says "round again" instead of "a
 * circle with an arrow stuck on it".
 *
 * Each arc spans 130° with 50° between them, of which the arrowhead eats about 20 — so
 * roughly 30° of clear air sits between one tip and the next arc's tail. The first attempt
 * used 30° gaps and the heads closed them entirely, which read as a solid ring.
 *
 * The whole thing is rotated 30° clockwise from the horizontal, so the break falls where
 * the eye is not already resting.
 */
export function Repeat(props: IconProps) {
  return (
    <Svg {...props} className="icon-repeat">
      <g {...STROKE}>
        <path d="M7.64 5.77 A7.6 7.6 0 0 1 19.57 12.66" />
        <path d="M16.36 18.23 A7.6 7.6 0 0 1 4.43 11.34" />
      </g>
      {/* A head at the end of each arc, pointing the way that arc was travelling. */}
      <path d="M19.27 16.05 L16.75 11.61 L22.53 12.11 Z" fill="currentColor" />
      <path d="M4.73 7.95 L7.25 12.39 L1.47 11.89 Z" fill="currentColor" />
    </Svg>
  );
}

/**
 * An alarm clock with a Z through it — snoozed.
 *
 * Bells and legs, so it reads as an *alarm* rather than as the time. That costs the case
 * some radius — 7.7 against the due date's 9.4 — and it ends up visibly smaller than the
 * calendar beside it. That is the right trade: the Z and the alarm are the point, and the
 * two are not meant to look like a pair.
 *
 * Still no hands. A face and a Z in the same circle is a smudge at this size, and the Z is
 * the half carrying the meaning.
 */
export function Snooze(props: IconProps) {
  return (
    <Svg {...props} className="icon-snooze">
      <g {...STROKE}>
        {/* Bells first, tucked behind the case. */}
        <path d="M2.9 6.6 A3.6 3.6 0 0 1 8 2.9" />
        <path d="M21.1 6.6 A3.6 3.6 0 0 0 16 2.9" />
        <circle cx="12" cy="13.3" r="7.7" />
        {/* Legs. */}
        <path d="M6.5 19.6 L4.6 22.1" />
        <path d="M17.5 19.6 L19.4 22.1" />
        <path d="M8.9 10.4 H15.1 L8.9 16.2 H15.1" strokeWidth="2.4" />
      </g>
    </Svg>
  );
}

/** A plus — adds something new. Two strokes, corner to corner of the box, crossing at centre. */
export function Plus(props: IconProps) {
  return (
    <Svg {...props} className="icon-plus">
      <g {...STROKE}>
        <path d="M12 4.4 V19.6" />
        <path d="M4.4 12 H19.6" />
      </g>
    </Svg>
  );
}

/**
 * A chevron pointing down — reveals a menu.
 *
 * One stroke, corner to corner of the box at 2.4 weight (the "one gesture" case, same as
 * the Z in snooze): a caret is nothing but its direction, so the whole icon is the line
 * that says which way.
 */
export function Chevron(props: IconProps) {
  return (
    <Svg {...props} className="icon-chevron">
      <path d="M4.8 8.4 L12 16.2 L19.2 8.4" {...STROKE} strokeWidth="2.4" />
    </Svg>
  );
}

/**
 * A calendar — a due date.
 *
 * Which is what a due date *is*, where a clock is a time of day. The date picker's own
 * calendar comes from the browser (`::-webkit-calendar-picker-indicator`, in shadow DOM),
 * so it cannot be reused here; this matches it in spirit and the house style in fact.
 *
 * Three dots rather than a grid of them: at 13px a 3×2 grid fills in and reads as a
 * smudged rectangle, and areas survive shrinking where hairlines do not.
 */
export function Calendar(props: IconProps) {
  return (
    <Svg {...props} className="icon-calendar">
      <g {...STROKE}>
        <rect x="2.6" y="4.8" width="18.8" height="16.6" rx="2.8" />
        <path d="M2.6 10.2 H21.4" />
        <path d="M7.7 2.4 V7.1" />
        <path d="M16.3 2.4 V7.1" />
      </g>
      <circle cx="7.7" cy="15.4" r="1.35" fill="currentColor" />
      <circle cx="12" cy="15.4" r="1.35" fill="currentColor" />
      <circle cx="16.3" cy="15.4" r="1.35" fill="currentColor" />
    </Svg>
  );
}

/**
 * A single arc with an arrowhead landing at its far end — skipping over, not repeating.
 * Deliberately not `Repeat`'s two-arc loop: a skip happens once and moves on, it doesn't
 * come back around.
 */
export function SkipArc(props: IconProps) {
  return (
    <Svg {...props} className="icon-skip">
      <path d="M3 17 A8 8 0 0 1 19 17" {...STROKE} />
      <path d="M15.5 12.5 L19.8 16.2 L14.8 18.4 Z" fill="currentColor" />
    </Svg>
  );
}

/**
 * A mini bar chart — three bars, centre tallest, left mid-height, right shortest. The
 * delta-distribution chart it links to (§7.1.3, §8.7) is rarely this tidy in practice, but
 * the icon's job is "history lives here," not a preview of the data.
 */
export function History(props: IconProps) {
  return (
    <Svg {...props} className="icon-history">
      <rect x="3.5" y="9" width="5" height="11" rx="1.4" fill="currentColor" />
      <rect x="9.5" y="3" width="5" height="17" rx="1.4" fill="currentColor" />
      <rect x="15.5" y="13" width="5" height="7" rx="1.4" fill="currentColor" />
    </Svg>
  );
}

/**
 * A magnifying glass — search.
 *
 * Lens and handle only, no crossbar: the two-stroke shape reads as "search" on its own, and
 * a field's own placeholder carries the rest of the meaning.
 */
export function Search(props: IconProps) {
  return (
    <Svg {...props} className="icon-search">
      <g {...STROKE}>
        <circle cx="10.4" cy="10.4" r="7.4" />
        <path d="M15.8 15.8 L21.4 21.4" />
      </g>
    </Svg>
  );
}

/** A circled "i" — what a badge means, on tap or click rather than a hover that a touch
 * screen or a stylus can't trigger. */
export function Info(props: IconProps) {
  return (
    <Svg {...props} className="icon-info">
      <circle cx="12" cy="12" r="9.4" {...STROKE} />
      <circle cx="12" cy="7.6" r="1.3" fill="currentColor" />
      <path d="M12 11 V17" {...STROKE} strokeWidth="2.4" />
    </Svg>
  );
}

/** The shackle path both `Lock` and `Unlock` share, so the two are provably the same size —
 * `Unlock` just rotates this one, rather than redrawing a differently-proportioned shape. */
const SHACKLE = "M7.5 11 V8 a4.5 4.5 0 0 1 9 0 V11";

/**
 * A closed padlock: a rounded case, a shackle looped in on both legs, and a keyhole — the
 * arrange-mode lock replacing what used to be the 🔒 emoji, which rendered as whatever the
 * installed emoji font decided (the same reason every icon in this file exists, per the
 * module docstring).
 */

export function Lock(props: IconProps) {
  return (
    <Svg {...props} className="icon-lock">
      <path d={SHACKLE} {...STROKE} />
      <rect x="4.8" y="11" width="14.4" height="10.2" rx="2.3" {...STROKE} />
      <circle cx="12" cy="15.2" r="1.6" fill="currentColor" />
      <path d="M12 16.8 V19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </Svg>
  );
}

/**
 * The same padlock, same shackle, pivoted open around the point where its left leg still
 * meets the case (7.5, 11) rather than redrawn to a different shape — a real shackle doesn't
 * change size when it swings. Genuinely taller than the rest of the set's 24-unit box
 * (`boxHeight`): rotated out, the free end reaches higher than the closed shackle's own
 * apex did, and arrange mode's unlocked button has the room for it now that the nav's page
 * pills sit in their own grouping boxes.
 */
export function Unlock(props: IconProps) {
  return (
    <Svg {...props} boxHeight={28} className="icon-unlock">
      <path d={SHACKLE} {...STROKE} transform="rotate(-30 7.5 11)" />
      <rect x="4.8" y="11" width="14.4" height="10.2" rx="2.3" {...STROKE} />
      <circle cx="12" cy="15.2" r="1.6" fill="currentColor" />
      <path d="M12 16.8 V19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </Svg>
  );
}

/**
 * A flag on a pole — the Next Action marker (§8.6.1). Filled when it's the current one,
 * hollow as the "make this the Next Action" control on any other subtask — same shape both
 * ways, the same pairing `Star` already uses for starred/not.
 */
export function Flag({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <Svg {...props} className="icon-flag">
      <path d="M6 3 V21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path
        d="M6 4.2 C10 2.2 13 6.2 18 4.2 V13.4 C13 15.4 10 11.4 6 13.4 Z"
        {...STROKE}
        fill={filled ? "currentColor" : "none"}
      />
    </Svg>
  );
}

/**
 * A single stem forking into two — splitting a subtask that turned out too big (§8.6.2).
 * The same control also creates a parent's first children, so the glyph reads as "break
 * this into pieces" generally rather than "split" specifically.
 */
export function Split(props: IconProps) {
  return (
    <Svg {...props} className="icon-split">
      <g {...STROKE}>
        <path d="M12 3.2 V10.4" />
        <path d="M12 10.4 C12 13.6 6.5 12.8 6.5 16.4 V19.4" />
        <path d="M12 10.4 C12 13.6 17.5 12.8 17.5 16.4 V19.4" />
      </g>
      <circle cx="12" cy="3.2" r="2.1" fill="currentColor" />
      <circle cx="6.5" cy="20.4" r="2.1" fill="currentColor" />
      <circle cx="17.5" cy="20.4" r="2.1" fill="currentColor" />
    </Svg>
  );
}

/**
 * A curved arrow turning back on itself — undo.
 *
 * New rather than borrowed, because nothing in this set meant it: `History` is a bar chart
 * (the *record* of something), `Cross` is dismiss, `Repeat` is recurrence. Every other undo
 * in EFSS is a control toggling its own meaning — the tick becomes undo — which works only
 * while the row survives and its control has two states. A step replaced by a split has
 * neither, so it needs a glyph of its own (DECISIONS.md D174).
 *
 * Drawn as a U-turn to the left: an arc doubling back, with the head at the end it returns
 * to, so the direction reads at 14px as well as it does large.
 */
export function Undo(props: IconProps) {
  return (
    <Svg {...props} className="icon-undo">
      <g {...STROKE}>
        <path d="M4.5 10.5 H14 a5 5 0 0 1 0 10 H9" />
        <path d="M8.2 6.6 4.3 10.5 8.2 14.4" />
      </g>
    </Svg>
  );
}

/**
 * A wrench — correcting a record after the fact (DECISIONS.md D144, D145). Not a
 * checkmark: the control it marks can resolve an occurrence as done, skipped, *or*
 * missed (§8.7's Fix control, `RecentOccurrences.tsx`), and a checkmark says only one of
 * those before the reader has chosen.
 */
export function Wrench(props: IconProps) {
  return (
    <Svg {...props} className="icon-wrench">
      <path
        {...STROKE}
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
      />
    </Svg>
  );
}

/**
 * A plain X — the third of the Fix form's three outcome buttons (D145): mark an
 * occurrence missed, alongside the checkmark (done) and `SkipArc` (skipped). Simple by
 * design; the other two glyphs in that trio already carry real shape, and a third one
 * competing for attention would read as decoration rather than a third equal choice.
 */
export function Cross(props: IconProps) {
  return (
    <Svg {...props} className="icon-cross">
      <g {...STROKE}>
        <path d="M5.5 5.5 L18.5 18.5" />
        <path d="M18.5 5.5 L5.5 18.5" />
      </g>
    </Svg>
  );
}

/**
 * A map pin — where a capture was made (§8.1).
 *
 * The one glyph in the set with a filled core: a hollow pin at this size reads as a badge
 * outline rather than a place marker, and the point is exactly what a stray decoration
 * cannot afford to blur.
 */
export function MapPin(props: IconProps) {
  return (
    <Svg {...props} className="icon-map-pin">
      <path
        {...STROKE}
        fill="currentColor"
        d="M12 21.4c-.5 0-4-4.3-6.4-8.2C4.1 10.8 3.4 9 3.4 7.4 3.4 4 6.6 1.6 12 1.6s8.6 2.4 8.6 5.8c0 1.6-.7 3.4-2.2 5.8-2.4 3.9-5.9 8.2-6.4 8.2Z"
      />
      <circle cx="12" cy="7.6" r="2.6" fill="var(--bg, #12131a)" stroke="none" />
    </Svg>
  );
}

/** Glow opacity per level — the outline is always drawn; only the fill inside the bulb
 * changes, so "off" reads as an unlit bulb rather than a missing one (§6.2's tier picker).
 * On and Bright are both fully lit — what tells them apart is Bright's rays, not the glass. */
const LIGHTBULB_GLOW: Record<"off" | "dim" | "on" | "bright", number> = {
  off: 0,
  dim: 0.35,
  on: 1,
  bright: 1,
};

/** The glass, centered and sized to leave room around it for Bright's rays — smaller than a
 * glyph filling the box the way the rest of the set does, on purpose: rays need somewhere to
 * emit into within the same 24-unit box every level shares, so the sizes stay identical as
 * the tier picker's face swaps between them. */
const LIGHTBULB_GLASS =
  "M12 6.4C9 6.4 6.8 8.5 6.8 11.1 6.8 12.8 7.7 14 8.5 14.9 9.1 15.6 9.5 16.1 9.5 16.7V17.6H14.5V16.7C14.5 16.1 14.9 15.6 15.5 14.9 16.3 14 17.2 12.8 17.2 11.1 17.2 8.5 15 6.4 12 6.4Z";

/**
 * Three stacked dots — reveals the overflow menu (`OverflowMenu.tsx`).
 *
 * Filled circles rather than an outline: at button size a stroked dot is a faint ring, and
 * this has to read as "more" at a glance the same way `Kebab`'s namesake skewer does —
 * three points, evenly spaced, nothing else competing for the eye.
 */
export function Kebab(props: IconProps) {
  return (
    <Svg {...props} className="icon-kebab">
      <circle cx="12" cy="4.8" r="2.4" fill="currentColor" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" />
      <circle cx="12" cy="19.2" r="2.4" fill="currentColor" />
    </Svg>
  );
}

/**
 * A set square — Planning mode.
 *
 * Not `Pencil`: that glyph already means "Edit" throughout the app, and reusing it for the
 * mode that makes Edit's own button visible again would have the same shape say two
 * different things a click apart. A drafting tool instead — the right triangle a plan gets
 * measured out with — corner to corner like `Star`/`Pencil`, with three tick marks graduating
 * the hypotenuse so it reads as an instrument rather than a random triangle.
 */
export function SetSquare(props: IconProps) {
  return (
    <Svg {...props} className="icon-set-square">
      <path d="M4 4 L4 20 L20 20 Z" {...STROKE} strokeLinejoin="round" />
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M7.7 9.9 L9.9 7.7" />
        <path d="M10.9 13.1 L13.1 10.9" />
        <path d="M14.1 16.3 L16.3 14.1" />
      </g>
    </Svg>
  );
}

/**
 * A lightning bolt — Execute mode.
 *
 * Solid rather than stroked, same technique as `Important`: a thin matching-colour stroke
 * just to round the joins, so the zigzag doesn't go razor-sharp at badge size. The one
 * universally "go, now" glyph, which is the whole point next to `SetSquare`'s
 * measure-first one.
 */
export function Bolt(props: IconProps) {
  return (
    <Svg {...props} className="icon-bolt">
      <path
        d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** The AI-breakdown tier picker's own face (§6.2, §11 item 9b) — one glyph, four brightness
 * levels, rather than a separate icon per tier. `level` is the resolved default tier, or
 * `"off"` when nothing currently fits the GPU budget (`aiTiers.ts::computeAvailability`). */
export function Lightbulb({
  level,
  ...props
}: IconProps & { level: "off" | "dim" | "on" | "bright" }) {
  return (
    <Svg {...props} className="icon-lightbulb">
      <path
        fill="currentColor"
        fillOpacity={LIGHTBULB_GLOW[level]}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d={LIGHTBULB_GLASS}
      />
      <path d="M9.7 18.4h4.6" {...STROKE} />
      <path d="M10.3 19.6h3.4" {...STROKE} />
      {/* The classic five-ray burst, only at Bright — top, both upper diagonals, left and
          right, none pointing down into where the base already reads as "plugged in." */}
      {level === "bright" && (
        <g {...STROKE}>
          <path d="M12 3.5V1" />
          <path d="M7.4 5.4 5.6 3.6" />
          <path d="M16.6 5.4 18.4 3.6" />
          <path d="M5.5 10H3" />
          <path d="M18.5 10H21" />
        </g>
      )}
    </Svg>
  );
}

/** A compact indeterminate progress glyph for actions that are still in flight. */
export function Spinner(props: IconProps) {
  return (
    <Svg {...props} className="icon-spinner">
      <circle cx="12" cy="12" r="8" {...STROKE} opacity="0.28" />
      <path d="M12 4a8 8 0 0 1 8 8" {...STROKE} />
    </Svg>
  );
}
